import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, isAuthenticated, getStoredUser, logout, login as authLogin, storeAuthData, LoginCredentials, logoutWithoutRedirect, samlLogin as authSamlLogin } from '../services/auth';
import { getOrganizations, getCurrentUser } from '../services/api';

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  error: string | null;
  organizations: any[];
  currentOrganization: any | null;
  login: (credentials: LoginCredentials) => Promise<any>;
  samlLogin: () => void;
  logout: (redirect?: boolean) => void;
  switchOrganization: (organization: any) => void;
  setCurrentOrg: (organization: any) => void;
  updateUser: (user: User) => void;
  handleTokenLogin: (accessToken: string, refreshToken: string) => Promise<any>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [currentOrganization, setCurrentOrganization] = useState<any | null>(null);

  useEffect(() => {
    const initAuth = async () => {
      if (isAuthenticated()) {
        try {
          const storedUser = getStoredUser();
          if (storedUser) {
            setUser(storedUser);
          } else {
            const currentUser = await getCurrentUser();
            setUser(currentUser);
            storeAuthData(currentUser);
          }

          const orgs = await getOrganizations();
          if (orgs && orgs.organizations && Array.isArray(orgs.organizations)) {
            setOrganizations(orgs.organizations);
            
            const storedCurrentOrg = localStorage.getItem('organization');
            if (storedCurrentOrg) {
              const currentOrg = JSON.parse(storedCurrentOrg);
              const refreshedOrg = orgs.organizations.find((o: any) => o.id === currentOrg.id);
              if (refreshedOrg) {
                setCurrentOrganization(refreshedOrg);
                localStorage.setItem('organization', JSON.stringify(refreshedOrg));
              }
            }
          }
        } catch (err) {
          console.error('Failed to get user data', err);
          logout();
        }
      }
      setLoading(false);
    };

    // Set initial state from localStorage synchronously
    const storedCurrentOrg = localStorage.getItem('organization');
    if (storedCurrentOrg) {
      setCurrentOrganization(JSON.parse(storedCurrentOrg));
    }

    initAuth();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authLogin(credentials);
      localStorage.setItem('token', response.access_token); // Store token first
      storeAuthData(response.user);

      const orgs = await getOrganizations();
      let selectedOrg = null;
      if (orgs && orgs.organizations && Array.isArray(orgs.organizations)) {
        setOrganizations(orgs.organizations);
        
        if (orgs.organizations.length === 1) {
          selectedOrg = orgs.organizations[0];
          localStorage.setItem('organization', JSON.stringify(selectedOrg));
        }
      }
      
      // Set user and organization state together to avoid race conditions
      setUser(response.user);
      if (selectedOrg) {
        setCurrentOrganization(selectedOrg);
      }
      
      return orgs;
    } catch (err) {
      console.error('Login failed', err);
      setError('Login failed. Please check your credentials and try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const samlLogin = () => {
    setLoading(true);
    setError(null);
    try {
      authSamlLogin();
    } catch (err) {
      console.error('SAML Login failed', err);
      setError('SAML Login failed. Please try again.');
      setLoading(false); // Stop loading on error
    }
    // No need for finally block if navigation occurs
  };

  const handleTokenLogin = async (accessToken: string, refreshToken: string) => {
    setLoading(true);
    setError(null);
    try {
      localStorage.setItem('token', accessToken);
      localStorage.setItem('refresh_token', refreshToken);
      const currentUser = await getCurrentUser();
      setUser(currentUser);
      storeAuthData(currentUser);
      const orgs = await getOrganizations();
      let selectedOrg = null;
      if (orgs && orgs.organizations && Array.isArray(orgs.organizations)) {
        setOrganizations(orgs.organizations);
        if (orgs.organizations.length === 1) {
          selectedOrg = orgs.organizations[0];
          localStorage.setItem('organization', JSON.stringify(selectedOrg));
        }
      }
      if (selectedOrg) {
        setCurrentOrganization(selectedOrg);
      }
      return orgs;
    } catch (err) {
      console.error('Token login failed', err);
      setError('Login failed. Please try again.');
      logout();
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const switchOrganization = async (organization: any) => {
    setLoading(true);
    localStorage.setItem('organization', JSON.stringify(organization));
    try {
      const updatedUser = await getCurrentUser(organization.id);
      updateUser(updatedUser);
      setCurrentOrganization(organization);
      window.location.reload();
    } catch (err) {
      console.error('Failed to update user on organization switch', err);
      setError('Failed to switch organization. Please try again.');
      setLoading(false);
    }
  };

  const setCurrentOrg = (organization: any) => {
    setCurrentOrganization(organization);
  }

  const handleLogout = (redirect = true) => {
    if (redirect) {
      logout();
    } else {
      logoutWithoutRedirect();
      setUser(null);
      setCurrentOrganization(null);
      setOrganizations([]);
    }
  };

  const updateUser = (newUser: User) => {
    setUser(newUser);
    storeAuthData(newUser);
  };

  const value = {
    isAuthenticated: !!user,
    user,
    loading,
    error,
    organizations,
    currentOrganization,
    login,
    samlLogin,
    logout: handleLogout,
    switchOrganization,
    handleTokenLogin,
    setCurrentOrg,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
